/**
 * Support of prop-types for `otp-ui` components was discontinued when that
 * project transition to TypeScript for type validation.  The final version of
 * those types have been ported and are now maintained here.  When trimet.org
 * moves to TypeScript otp-ui types can again be imported from that library
 *
 * The types in this file are only those actively used by this application, if
 * others are needed in future development the last version of the otp-ui
 * prop-types can be found here:
 * https://github.com/opentripplanner/otp-ui/blob/%40opentripplanner/core-utils-v4.11.5/packages/core-utils/src/types.js
 */

import PropTypes from 'prop-types';

const alertType = PropTypes.shape({
  alertHeaderText: PropTypes.string,
  alertDescriptionText: PropTypes.string,
  alertUrl: PropTypes.string,
  effectiveStartDate: PropTypes.number,
});

export const companyType = PropTypes.shape({
  /** The id of the company. This is typically in all-caps */
  id: PropTypes.string.isRequired,
  /** A human-readable text value that can be displayed to users */
  label: PropTypes.string.isRequired,
  /**
   * A comma-separated list of applicable modes of travel that the company
   * offers
   */
  modes: PropTypes.string.isRequired,
});

const elevationType = PropTypes.oneOfType([
  PropTypes.arrayOf(
    PropTypes.shape({
      first: PropTypes.number.isRequired,
      second: PropTypes.number.isRequired,
    }).isRequired
  ),
  PropTypes.string,
]);

export const encodedPolylineType = PropTypes.shape({
  length: PropTypes.number.isRequired,
  points: PropTypes.string.isRequired,
});

const feedScopedIdType = PropTypes.shape({
  agencyId: PropTypes.string,
  id: PropTypes.string,
});

const moneyType = PropTypes.shape({
  cents: PropTypes.number.isRequired,
  currency: PropTypes.shape({
    defaultFractionDigits: PropTypes.number.isRequired,
    currencyCode: PropTypes.string.isRequired,
    symbol: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
  }).isRequired,
});

/**
 * Represents the fare component of an itinerary of an OTP plan response. See
 * detailed documentation in OTP webservice documentation here:
 * http://otp-docs.ibi-transit.com/api/json_Fare.html
 *
 * NOTE: so far the fare includes ONLY a fare encountered on public transit and
 * not any bike rental or TNC rental fees.
 */
export const fareType = PropTypes.shape({
  details: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        fareId: PropTypes.oneOfType([PropTypes.string, feedScopedIdType])
          .isRequired,
        price: moneyType.isRequired,
        routes: PropTypes.arrayOf(
          PropTypes.oneOfType([PropTypes.string, feedScopedIdType])
        ).isRequired,
      })
    ).isRequired
  ),
  fare: PropTypes.objectOf(moneyType),
});

export const placeType = PropTypes.shape({
  arrival: PropTypes.number,
  departure: PropTypes.number,
  lat: PropTypes.number.isRequired,
  lon: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  networks: PropTypes.arrayOf(PropTypes.string.isRequired),
  stopCode: PropTypes.string,
  stopId: PropTypes.string,
  stopIndex: PropTypes.number,
  stopSequence: PropTypes.number,
  vertexType: PropTypes.string.isRequired,
  zoneId: PropTypes.string,
});

/**
 * Represents steps in a leg in an itinerary of an OTP plan response. These are
 * only for non-transit modes. See documentation here:
 * http://otp-docs.ibi-transit.com/api/json_WalkStep.html
 */
export const stepsType = PropTypes.arrayOf(
  PropTypes.shape({
    absoluteDirection: PropTypes.string,
    alerts: PropTypes.arrayOf(alertType),
    area: PropTypes.bool.isRequired,
    bogusName: PropTypes.bool.isRequired,
    distance: PropTypes.number.isRequired,
    elevation: elevationType.isRequired,
    lat: PropTypes.number.isRequired,
    lon: PropTypes.number.isRequired,
    relativeDirection: PropTypes.string.isRequired,
    stayOn: PropTypes.bool.isRequired,
    streetName: PropTypes.string.isRequired,
  })
);

/**
 * Represents a leg in an itinerary of an OTP plan response. Each leg represents
 * a portion of the overall itinerary that is done until either reaching the
 * destination or transitioning to another mode of travel. See OTP webservice
 * documentation here:
 * http://otp-docs.ibi-transit.com/api/json_Leg.html
 */
export const legType = PropTypes.shape({
  agencyId: PropTypes.string,
  agencyName: PropTypes.string,
  agencyTimeZoneOffset: PropTypes.number.isRequired,
  agencyUrl: PropTypes.string,
  alerts: PropTypes.arrayOf(alertType),
  arrivalDelay: PropTypes.number.isRequired,
  departureDelay: PropTypes.number.isRequired,
  distance: PropTypes.number.isRequired,
  duration: PropTypes.number.isRequired,
  endTime: PropTypes.number.isRequired,
  from: placeType.isRequired,
  hailedCar: PropTypes.bool,
  headsign: PropTypes.string,
  interlineWithPreviousLeg: PropTypes.bool.isRequired,
  intermediateStops: PropTypes.arrayOf(placeType),
  interStopGeometry: PropTypes.arrayOf(encodedPolylineType),
  legGeometry: encodedPolylineType.isRequired,
  mode: PropTypes.string.isRequired,
  pathway: PropTypes.bool.isRequired,
  realTime: PropTypes.bool.isRequired,
  rentedBike: PropTypes.bool,
  rentedCar: PropTypes.bool,
  rentedVehicle: PropTypes.bool,
  route: PropTypes.string,
  routeId: PropTypes.string,
  routeType: PropTypes.number,
  serviceDate: PropTypes.string,
  startTime: PropTypes.number.isRequired,
  steps: stepsType.isRequired,
  tncData: PropTypes.shape({
    company: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
    displayName: PropTypes.string.isRequired,
    estimatedArrival: PropTypes.number.isRequired,
    maxCost: PropTypes.number.isRequired,
    minCost: PropTypes.number.isRequired,
    productId: PropTypes.string.isRequired,
    travelDuration: PropTypes.number.isRequired,
  }),
  to: placeType.isRequired,
  transitLeg: PropTypes.bool.isRequired,
  tripBlockId: PropTypes.string,
  tripId: PropTypes.string,
});

/**
 * Represents an itinerary of an OTP plan response. See detailed documentation
 * in OTP webservice documentation here:
 * http://otp-docs.ibi-transit.com/api/json_Itinerary.html
 */
export const itineraryType = PropTypes.shape({
  duration: PropTypes.number.isRequired,
  elevationGained: PropTypes.number.isRequired,
  elevationLost: PropTypes.number.isRequired,
  endTime: PropTypes.number.isRequired,
  fare: fareType,
  legs: PropTypes.arrayOf(legType).isRequired,
  startTime: PropTypes.number.isRequired,
  tooSloped: PropTypes.bool,
  transfers: PropTypes.number.isRequired,
  transitTime: PropTypes.number.isRequired,
  waitingTime: PropTypes.number.isRequired,
  walkDistance: PropTypes.number.isRequired,
  walkLimitExceeded: PropTypes.bool.isRequired,
  walkTime: PropTypes.number.isRequired,
});

/**
 * This models data about a vehicle rental station as obtained from various
 * vehicle rental API endpoints from OTP.
 */
export const stationType = PropTypes.shape({
  bikesAvailable: PropTypes.number,
  id: PropTypes.string.isRequired,
  isFloatingBike: PropTypes.bool,
  isFloatingCar: PropTypes.bool,
  isFloatingVehicle: PropTypes.bool,
  name: PropTypes.string,
  networks: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  spacesAvailable: PropTypes.number,
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
});

/**
 * Describes some options to help display data about a transit agency that is
 * configured in an opentripplanner instance.
 */
export const transitOperatorType = PropTypes.shape({
  agencyId: PropTypes.string.isRequired,
  defaultRouteColor: PropTypes.string,
  defaultRouteTextColor: PropTypes.string,
  feedId: PropTypes.string.isRequired,
  logo: PropTypes.string,
  longNameSplitter: PropTypes.string,
  name: PropTypes.string,
  order: PropTypes.number,
});

/** Describes the objects from the real-time vehicle service */
export const transitVehicleType = PropTypes.shape({
  id: PropTypes.string,
  agencyId: PropTypes.string,
  routeId: PropTypes.string,
  routeShortName: PropTypes.string,
  routeLongName: PropTypes.string,
  routeType: PropTypes.string,
  status: PropTypes.string,
  reportDate: PropTypes.string,
  seconds: PropTypes.number,
  stopSequence: PropTypes.number,
  stopId: PropTypes.string,
  vehicleId: PropTypes.string,
  tripId: PropTypes.string,
  blockId: PropTypes.string,
  directionId: PropTypes.string,
  lat: PropTypes.number,
  lon: PropTypes.number,
  heading: PropTypes.number,
  lastLocId: PropTypes.number,
  nextLocId: PropTypes.number,
  signMessage: PropTypes.string,
});

/**
 * Defines which symbol to render based on a zoom level, and optionally by
 * entity type. (Only one symbol is rendered for any zoom level.)
 */
export const zoomBasedSymbolType = PropTypes.shape({
  /**
   * A function with the signature (entity: object) => string
   * that determines the type of an entity.
   * symbolByType and getType must be either be both specified or both omitted.
   */
  getType: PropTypes.func,
  /**
   * The zoom level beginning at which the marker is drawn,
   * unless another marker with a higher minZoom is met.
   */
  minZoom: PropTypes.number.isRequired,
  /**
   * The symbol-representing component to draw, with the signature
   * ({ entity: object, zoom: number }) => Element
   * where entity must have an id attribute and contain coordinates information
   * for placement on the map.
   */
  symbol: PropTypes.elementType.isRequired,
  /**
   * The symbol-representing component to draw for each entity type,
   * with the same signature as symbol. If a type returned by getType() is not
   * listed, then the component defined in the 'symbol' attribute will be
   * rendered by default. symbolByType and getType must be either be both
   * specified or both omitted.
   */
  symbolByType: PropTypes.objectOf(PropTypes.elementType),
});

export const vehicleRentalMapOverlaySymbolsType = PropTypes.arrayOf(
  PropTypes.oneOfType([
    PropTypes.shape({
      dockStrokeColor: PropTypes.string,
      fillColor: PropTypes.string,
      minZoom: PropTypes.number.isRequired,
      pixels: PropTypes.number,
      type: PropTypes.string.isRequired,
    }),
    zoomBasedSymbolType,
  ]).isRequired
);

/** Represents the expected configuration of the web app */
export const appConfigType = PropTypes.shape({
  companies: PropTypes.arrayOf(companyType.isRequired),
  dateTime: PropTypes.shape({
    timeFormat: PropTypes.string,
    dateFormat: PropTypes.string,
  }),
  // TODO: this is an incomplete type mapping, add full typing
  map: PropTypes.shape({
    overlays: PropTypes.arrayOf(
      PropTypes.shape({
        /**
         * The applicable companies this overlay covers. Only applicable in
         * certain vehicle rental overlays.
         */
        companies: PropTypes.arrayOf(PropTypes.string.isRequired),
        name: PropTypes.string.isRequired,
        /**
         * The applicable map symbols. Only applicable in vehicle rental
         * overlays.
         */
        mapSymbols: vehicleRentalMapOverlaySymbolsType,
        /**
         * Only used during park and ride queries. This will filter out P&Rs
         * that are further than the specified number of meters from a transit
         * stop.
         */
        maxTransitDistance: PropTypes.number,
        /**
         * The applicable modes this overlay covers. Only applicable in certain
         * vehicle rental overlays.
         */
        modes: PropTypes.arrayOf(PropTypes.string.isRequired),
        /**
         * The type of overlay. Currently valid values include:
         *
         * "bike-rental", "car-rental", "micromobility-rental", "park-and-ride",
         * "stops", "tile"
         */
        type: PropTypes.string.isRequired,
      })
    ),
  }),
  transitOperators: PropTypes.arrayOf(transitOperatorType),
});
